import React, { useState } from 'react';
import { toastError, toastSuccess } from '../utils/toast';
import { HttpError } from '@flashbacktech/flashbackclient/api';
import { addProviderStat } from '../services/ProviderStatsService';
import { useApi } from '../contexts/ApiContext';
import { useAuth, useWorkspaces, useSelectedWorkspace } from '../contexts/AuthContext';
import { useWithTokenRefresh } from '../utils/api';
import { ApiTypes } from '@flashbacktech/flashbackclient/api';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Paper,
  IconButton,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';

// Logos des fournisseurs cloud
import googleCloudLogo from '../assets/cloud-providers/google-cloud-icon.svg';
import awsLogo from '../assets/cloud-providers/aws.svg';
import azureLogo from '../assets/cloud-providers/azure.svg';
import filecoinLogo from '../assets/cloud-providers/filecoin.svg';
import storejLogo from '../assets/cloud-providers/storej.svg';
import walrusLogo from '../assets/cloud-providers/walrus.svg';

// Interface pour les fournisseurs cloud
interface CloudProvider {
  id: string;
  name: string;
  logo: string;
  description: string;
}

// Liste des fournisseurs cloud disponibles
const cloudProviders: CloudProvider[] = [
  {
    id: 'google-cloud',
    name: 'Google Cloud',
    logo: googleCloudLogo,
    description: 'Vivamus vehicula dui non orci accumsan consectetur in eu est.',
  },
  {
    id: 'aws',
    name: 'AWS',
    logo: awsLogo,
    description: 'Vivamus vehicula dui non orci accumsan consectetur in eu est.',
  },
  {
    id: 'azure',
    name: 'Azure',
    logo: azureLogo,
    description: 'Vivamus vehicula dui non orci accumsan consectetur in eu est.',
  },
  {
    id: 'filecoin',
    name: 'Filecoin',
    logo: filecoinLogo,
    description: 'Vivamus vehicula dui non orci accumsan consectetur in eu est.',
  },
  {
    id: 'storej',
    name: 'Storj',
    logo: storejLogo,
    description: 'Vivamus vehicula dui non orci accumsan consectetur in eu est.',
  },
  {
    id: 'walrus',
    name: 'Walrus',
    logo: walrusLogo,
    description: 'Vivamus vehicula dui non orci accumsan consectetur in eu est.',
  },
  {
    id: 'other',
    name: 'Other',
    logo: '',
    description: 'Vivamus vehicula dui non orci accumsan consectetur in eu est.',
  },
];

// Types de stockage disponibles
const storageTypes = [
  { id: 'standard', name: 'Standard Storage' },
  { id: 'cold', name: 'Cold Storage' },
  { id: 'archive', name: 'Archive Storage' },
];

// Étapes du processus d'ajout de bucket
const steps = ['Select provider', 'Bucket details', 'Configuration', 'Review'];

const AddBucket: React.FC = () => {
  const navigate = useNavigate();
  const { apiClient } = useApi();
  const { isAuthenticated, authState, setSelectedWorkspace } = useAuth();
  const withTokenRefresh = useWithTokenRefresh();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [bucketName, setBucketName] = useState('');
  const [bucketId, setBucketId] = useState('');
  const [storageType, setStorageType] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Use centralized workspace hooks
  const workspaces = useWorkspaces();
  const selectedWorkspaceId = useSelectedWorkspace();

  // Common fields comme dans StorageUnits.tsx
  const [newUnit, setNewUnit] = useState<Partial<ApiTypes.StorageUnit>>({
    name: '',
    bucket: '',
    storageType: ApiTypes.StorageType.S3,
    status: 'UNKNOWN',
  });

  // Champs spécifiques aux providers
  const [s3Fields, setS3Fields] = useState({
    accessKey: '',
    secret: '',
    region: '',
    endpoint: '',
    bucket: '',
  });

  const [gcsFields, setGcsFields] = useState({
    clientEmail: '',
    privateKey: '',
    projectId: '',
    bucket: '',
  });

  const [azureFields, setAzureFields] = useState({
    storageAccount: '',
    accessKey: '',
    container: '',
  });

  // Gestion des étapes
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Réinitialiser le formulaire
  const resetForm = () => {
    setBucketName('');
    setBucketId('');
    setNewUnit({
      name: '',
      bucket: '',
      storageType: ApiTypes.StorageType.S3,
      status: 'UNKNOWN',
    });
    setS3Fields({
      accessKey: '',
      secret: '',
      region: '',
      endpoint: '',
      bucket: '',
    });
    setGcsFields({
      clientEmail: '',
      privateKey: '',
      projectId: '',
      bucket: '',
    });
    setAzureFields({
      storageAccount: '',
      accessKey: '',
      container: '',
    });
    // Conserver le workspace sélectionné
  };

  // Workspaces are now loaded from AuthContext via useWorkspaces() hook
  // No need to load them here - they're already cached in globalData

  // Validation du formulaire comme dans StorageUnits.tsx
  const [validationErrors, setValidationErrors] = useState<{
    name?: string;
    bucket?: string;
    key?: string;
    secret?: string;
    region?: string;
    clientEmail?: string;
    privateKey?: string;
    storageAccount?: string;
    container?: string;
    accessKey?: string;
  }>({});

  const validateForm = (bEdit = false) => {
    const errors: typeof validationErrors = {};

    // Common validation
    if (!bucketName?.trim()) {
      errors.name = 'Name is required';
    }

    // Storage type specific validation
    let apiStorageType = ApiTypes.StorageType.S3;
    if (selectedProvider === 'google-cloud') apiStorageType = ApiTypes.StorageType.GCS;
    else if (selectedProvider === 'azure') apiStorageType = ApiTypes.StorageType.BLOB;

    switch (apiStorageType) {
      case ApiTypes.StorageType.S3:
        if (!s3Fields.accessKey?.trim()) {
          errors.accessKey = 'Access Key is required';
        }
        if (!s3Fields.bucket?.trim() && !bucketId?.trim()) {
          errors.bucket = 'Bucket is required';
        }
        if (!s3Fields.secret?.trim()) {
          errors.secret = 'Secret Key is required';
        }
        break;
      case ApiTypes.StorageType.GCS:
        if (!gcsFields.clientEmail?.trim()) {
          errors.clientEmail = 'Client Email is required';
        }
        if (!gcsFields.bucket?.trim() && !bucketId?.trim()) {
          errors.bucket = 'Bucket is required';
        }
        if (!gcsFields.privateKey?.trim()) {
          errors.privateKey = 'Private Key is required';
        }
        break;
      case ApiTypes.StorageType.BLOB:
        if (!azureFields.storageAccount?.trim()) {
          errors.storageAccount = 'Storage Account is required';
        }
        if (!azureFields.container?.trim() && !bucketId?.trim()) {
          errors.container = 'Container is required';
        }
        if (!bEdit && !azureFields.accessKey?.trim()) {
          errors.accessKey = 'Access Key is required';
        }
        break;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Sélection d'un fournisseur
  const handleProviderSelect = (providerId: string) => {
    setSelectedProvider(providerId);
    handleNext();
  };

  // Annulation et retour à la page précédente
  const handleCancel = () => {
    navigate(-1);
  };

  // Fonctions d'aide pour obtenir les valeurs en fonction du type de stockage
  const getBucket = (storageType: ApiTypes.StorageType) => {
    switch (storageType) {
      case ApiTypes.StorageType.S3:
        return s3Fields.bucket || bucketId || bucketName;
      case ApiTypes.StorageType.GCS:
        return gcsFields.bucket || bucketId || bucketName;
      case ApiTypes.StorageType.BLOB:
        return azureFields.container;
    }
  };

  const getKey = (storageType: ApiTypes.StorageType) => {
    switch (storageType) {
      case ApiTypes.StorageType.S3:
        return s3Fields.accessKey;
      case ApiTypes.StorageType.GCS:
        return gcsFields.clientEmail;
      case ApiTypes.StorageType.BLOB:
        return azureFields.storageAccount;
    }
  };

  const getSecret = (storageType: ApiTypes.StorageType) => {
    switch (storageType) {
      case ApiTypes.StorageType.S3:
        return s3Fields.secret;
      case ApiTypes.StorageType.GCS:
        return gcsFields.privateKey;
      case ApiTypes.StorageType.BLOB:
        return azureFields.accessKey;
    }
  };

  // Soumission du formulaire
  const handleSubmit = async () => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }

    if (!selectedWorkspaceId) {
      toastError('No accessible workspace. Please create or get access to a workspace first.');
      return;
    }

    // Mettre à jour newUnit avec le nom du bucket et le type de stockage
    let apiStorageType = ApiTypes.StorageType.S3;
    if (selectedProvider === 'google-cloud') apiStorageType = ApiTypes.StorageType.GCS;
    else if (selectedProvider === 'azure') apiStorageType = ApiTypes.StorageType.BLOB;

    setNewUnit({
      ...newUnit,
      name: bucketName,
      storageType: apiStorageType,
    });

    // Mettre à jour le bucket ID dans les champs spécifiques
    if (apiStorageType === ApiTypes.StorageType.S3) {
      setS3Fields({ ...s3Fields, bucket: bucketId || bucketName });
    } else if (apiStorageType === ApiTypes.StorageType.GCS) {
      setGcsFields({ ...gcsFields, bucket: bucketId || bucketName });
    } else if (apiStorageType === ApiTypes.StorageType.BLOB) {
      // Pour Azure, le container est le bucket
      setAzureFields({ ...azureFields, container: bucketId || bucketName });
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Créer la requête exactement comme dans StorageUnits.tsx
      const createStorageBucket: ApiTypes.CreateBucketRequest = {
        name: bucketName,
        bucket: getBucket(apiStorageType),
        storageType: apiStorageType,
        key: getKey(apiStorageType),
        secret: getSecret(apiStorageType),
        workspaceId: selectedWorkspaceId,
      };

      if (apiStorageType === ApiTypes.StorageType.S3) {
        if (s3Fields.region) createStorageBucket.region = s3Fields.region;
        if (s3Fields.endpoint) createStorageBucket.endpoint = s3Fields.endpoint;
      }

      const response = await withTokenRefresh(() =>
        apiClient.createStorageBucket(createStorageBucket)
      );

      if (response.success) {
        // Stocker le provider sélectionné dans le localStorage
        if (selectedProvider) {
          // Trouver le nom du provider à partir de son ID
          const providerInfo = cloudProviders.find((p) => p.id === selectedProvider);
          if (providerInfo) {
            // Ajouter une statistique pour ce provider avec une taille par défaut (1GB)
            addProviderStat(selectedProvider, providerInfo.name);
          }
        }

        // Sauvegarder l'ID de la nouvelle unité dans sessionStorage
        if (response.bucketId) {
          // Récupérer les IDs existants ou initialiser un tableau vide
          const existingBuckets = sessionStorage.getItem('newlyCreatedBuckets');
          const newBucketIds = existingBuckets ? JSON.parse(existingBuckets) : [];

          // Ajouter le nouvel ID
          newBucketIds.push(response.bucketId);

          // Sauvegarder dans sessionStorage
          sessionStorage.setItem('newlyCreatedBuckets', JSON.stringify(newBucketIds));
          // Backward compatibility write to old key as well
          try {
            const existingUnits = sessionStorage.getItem('newlyCreatedUnits');
            const legacyIds = existingUnits ? JSON.parse(existingUnits) : [];
            legacyIds.push(response.bucketId);
            sessionStorage.setItem('newlyCreatedUnits', JSON.stringify(legacyIds));
          } catch (e) {
            // ignore
          }
        }

        // Reset all fields
        resetForm();
        setValidationErrors({});

        // Afficher un message de succès
        toastSuccess('Bucket created successfully');

        // Rediriger vers la liste des buckets après création
        navigate('/cloud-storage/buckets');
      } else {
        toastError('Failed to create bucket. Please try again.');
      }
    } catch (error) {
      if (error instanceof HttpError) {
        switch (error.status) {
          case 404:
            toastError('Bucket not found');
            break;
          case 400:
            toastError('Bucket name must be unique');
            break;
          default:
            toastError(`Failed to create bucket: ${error.message}`);
        }
      } else {
        toastError('Failed to create bucket. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: '1200px', mx: 'auto' }}>
      {/* En-tête */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={handleCancel} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
            Add
          </Typography>
        </Box>
        <Button variant="outlined" color="inherit" onClick={handleCancel} startIcon={<CloseIcon />}>
          Cancel
        </Button>
      </Box>

      {/* Description */}
      <Typography variant="body1" sx={{ mb: 4, color: '#546e7a' }}>
        Follow the simple steps below to create a bucket.
      </Typography>

      {/* Stepper */}
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Divider sx={{ mb: 4 }} />

      {/* Contenu principal basé sur l'étape active */}
      <Box sx={{ mt: 2 }}>
        {activeStep === 0 && (
          <>
            {/* Workspace selector */}
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, fontSize: '16px', lineHeight: '24px', color: '#000', mr: 2 }}
              >
                Workspace
              </Typography>
              <FormControl sx={{ minWidth: 260 }} size="small">
                <InputLabel id="workspace-select-label">Select workspace</InputLabel>
                <Select
                  labelId="workspace-select-label"
                  label="Select workspace"
                  value={selectedWorkspaceId}
                  onChange={(e) => {
                    const val = e.target.value as string;
                    // Use centralized setSelectedWorkspace from AuthContext
                    setSelectedWorkspace(val);
                  }}
                >
                  {workspaces.length === 0 && (
                    <MenuItem value="" disabled>
                      No accessible workspace
                    </MenuItem>
                  )}
                  {workspaces.map((w) => (
                    <MenuItem key={w.id} value={w.id}>
                      {w.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, fontSize: '20px', lineHeight: '30px', color: '#000' }}
              >
                Select provider
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  ml: 2,
                  color: '#535862',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '20px',
                }}
              >
                Start by selecting a provider; this helps us tailor your process.
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {cloudProviders.map((provider) => (
                <Grid item xs={12} sm={6} md={4} key={provider.id}>
                  <Card
                    sx={{
                      width: 344,
                      height: 205,
                      minWidth: 320,
                      borderRadius: '12px',
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      borderColor: 'divider',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                      },
                    }}
                    onClick={() => handleProviderSelect(provider.id)}
                  >
                    <CardContent sx={{ p: 3, flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          {provider.id !== 'other' ? (
                            <Box
                              component="img"
                              src={provider.logo}
                              alt={provider.name}
                              sx={{
                                height: 40,
                                width: 'auto',
                                maxWidth: 120,
                                objectFit: 'contain',
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                height: 40,
                                width: 40,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px dashed #ccc',
                                color: '#666',
                              }}
                            >
                              +
                            </Box>
                          )}
                          <Typography variant="h6" sx={{ ml: provider.id !== 'other' ? 2 : 1 }}>
                            {provider.name}
                          </Typography>
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                          {provider.description}
                        </Typography>
                      </Box>
                    </CardContent>
                    <Box
                      sx={{
                        height: 53,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        px: 3,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Button
                        variant="text"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleProviderSelect(provider.id);
                        }}
                      >
                        Select provider
                      </Button>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {activeStep === 1 && (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Bucket details
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Next, fill in the details for the bucket
              </Typography>
            </Box>

            <Paper sx={{ p: 3, mb: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Bucket name"
                    placeholder="Add a unique name"
                    value={bucketName}
                    onChange={(e) => setBucketName(e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Bucket ID"
                    placeholder="Bucket ID"
                    value={bucketId}
                    onChange={(e) => setBucketId(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Storage type</InputLabel>
                    <Select
                      value={storageType}
                      label="Storage type"
                      onChange={(e) => setStorageType(e.target.value)}
                    >
                      {storageTypes.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                          {type.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Champs spécifiques à AWS S3 */}
                {selectedProvider === 'aws' && (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Access Key"
                        placeholder="Enter AWS access key"
                        value={s3Fields.accessKey}
                        onChange={(e) => setS3Fields({ ...s3Fields, accessKey: e.target.value })}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Secret Key"
                        placeholder="Enter AWS secret key"
                        value={s3Fields.secret}
                        onChange={(e) => setS3Fields({ ...s3Fields, secret: e.target.value })}
                        type="password"
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Region"
                        placeholder="e.g., us-east-1"
                        value={s3Fields.region}
                        onChange={(e) => setS3Fields({ ...s3Fields, region: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Endpoint (optional)"
                        placeholder="Custom endpoint URL"
                        value={s3Fields.endpoint}
                        onChange={(e) => setS3Fields({ ...s3Fields, endpoint: e.target.value })}
                      />
                    </Grid>
                  </>
                )}

                {/* Champs spécifiques à Google Cloud Storage */}
                {selectedProvider === 'google-cloud' && (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Client Email"
                        placeholder="Enter service account email"
                        value={gcsFields.clientEmail}
                        onChange={(e) =>
                          setGcsFields({ ...gcsFields, clientEmail: e.target.value })
                        }
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Private Key"
                        placeholder="Enter private key"
                        value={gcsFields.privateKey}
                        onChange={(e) => setGcsFields({ ...gcsFields, privateKey: e.target.value })}
                        type="password"
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Project ID"
                        placeholder="Enter Google Cloud project ID"
                        value={gcsFields.projectId}
                        onChange={(e) => setGcsFields({ ...gcsFields, projectId: e.target.value })}
                      />
                    </Grid>
                  </>
                )}

                {/* Champs spécifiques à Azure Blob Storage */}
                {selectedProvider === 'azure' && (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Storage Account"
                        placeholder="Enter Azure storage account name"
                        value={azureFields.storageAccount}
                        onChange={(e) =>
                          setAzureFields({ ...azureFields, storageAccount: e.target.value })
                        }
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Access Key"
                        placeholder="Enter Azure access key"
                        value={azureFields.accessKey}
                        onChange={(e) =>
                          setAzureFields({ ...azureFields, accessKey: e.target.value })
                        }
                        type="password"
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Container Name"
                        placeholder="Enter Azure container name"
                        value={azureFields.container}
                        onChange={(e) =>
                          setAzureFields({ ...azureFields, container: e.target.value })
                        }
                        required
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Paper>
          </>
        )}

        {/* Vous pouvez ajouter d'autres étapes ici selon vos besoins */}

        {/* Boutons de navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button variant="outlined" onClick={handleCancel} sx={{ mr: 1 }}>
            Cancel
          </Button>
          <Box>
            {activeStep > 0 && (
              <Button variant="outlined" onClick={handleBack} sx={{ mr: 1 }}>
                Back
              </Button>
            )}
            {activeStep < steps.length - 1 ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleNext}
                disabled={
                  (activeStep === 0 && !selectedProvider) ||
                  (activeStep === 1 && (!bucketName || !storageType))
                }
              >
                Next
              </Button>
            ) : (
              <Button variant="contained" color="primary" onClick={handleSubmit}>
                Add
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AddBucket;
